import React from 'react';
import { Tooltip } from '@mui/material';
import StatusBadge from './StatusBadge';
import { FittingStatus } from '../api/fittings';
import { C } from '../theme/tokens';

/**
 * How far along a fit is, in one mark.
 *
 * This exists because "here is a fit for this site" is a claim, and the
 * claims differ enormously in weight: somebody's half-finished paste and a
 * doctrine the corp has cleared a hundred C5s in look identical in a list of
 * names. The badge is what separates them at a glance, and it is why the
 * suggestion list is worth trusting at all.
 *
 * Colour follows the app's existing status semantics rather than inventing a
 * fourth scale — green is "good to go", gold is "in flight, unproven", red is
 * "not ready", neutral-dim is "no data", exactly as every other badge in the
 * console reads. Unrated is deliberately visible instead of hidden: a missing
 * badge would read as an oversight, where "unrated" reads as the honest
 * absence of a claim.
 */
const STATUS: Record<FittingStatus, { label: string; accent: string; hint: string }> = {
  working: {
    label: 'working',
    accent: C.green,
    hint: 'Flown here and proven. This is the one to undock.',
  },
  testing: {
    label: 'testing',
    accent: C.gold,
    hint: 'Being flown and judged. Expect it to change, and expect surprises.',
  },
  wip: {
    label: 'WIP',
    accent: C.red,
    hint: 'Still being built. Read it for ideas, do not undock it.',
  },
};

const UNRATED = {
  label: 'unrated',
  accent: C.neutral,
  hint: 'Nobody has vouched for this fit yet — it is here because it was tagged for this site, not because it has been flown.',
};

const FittingStatusBadge: React.FC<{ status: FittingStatus | null }> = ({ status }) => {
  const meta = status ? STATUS[status] : UNRATED;
  return (
    <Tooltip title={meta.hint}>
      <span>
        <StatusBadge label={meta.label} accent={meta.accent} dim={!status} />
      </span>
    </Tooltip>
  );
};

export default FittingStatusBadge;
